import { createReducer, createSelector } from '@reduxjs/toolkit';

import {
  assertHasProperties,
  assertHasProperty,
  assertIsOneOfTypes,
  assertIsString,
} from 'common/assertions';
import type { StorytellerState } from 'store/StorytellerReduxState';

import { createStory, saveStory, prepareStoryForTemplate } from 'store/TopLevelActions';

export interface DigestState {
  storyDigest: string;
  templateDigest: string;
  templateChangedButHasNotSaved: boolean;
}

const initialState: DigestState = {
  storyDigest: '',
  templateDigest: '',
  templateChangedButHasNotSaved: false,
};

// The digest reducer is used to keep track of the digest of the story for undo and redo purposes.
// The digest is the unique identifier for the story, and is used to get story snapshot.
export const digestReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(createStory, (state: DigestState, action) => {
      const { payload } = action;

      const storyData = payload.data || payload;

      state.storyDigest = storyData.digest;
      state.templateDigest = storyData.templateDigest;
      state.templateChangedButHasNotSaved = storyData.templateChangedButHasNotSaved;
    })
    .addCase(saveStory, (state, action) => {
      const { payload } = action;
      assertHasProperty(payload, 'digest');

      const { digest } = payload;
      assertIsString(digest);

      if (state.templateChangedButHasNotSaved) {
        state.templateDigest = digest;
        state.templateChangedButHasNotSaved = false;
      }

      state.storyDigest = digest;
    })
    .addCase(prepareStoryForTemplate, (state, action) => {
      const { payload } = action;
      assertHasProperties(payload, 'templateContent', 'layout');

      const { templateContent, layout } = payload;
      assertIsOneOfTypes(templateContent, 'object');
      assertIsOneOfTypes(layout, 'string');

      state.templateChangedButHasNotSaved = true;
    });
});

export const selectDigest = (state: StorytellerState) => state.digest;

export const selectStoryDigest = createSelector(selectDigest, (slice) => slice.storyDigest ?? '');
export const selectTemplateDigest = createSelector(selectDigest, (slice) => slice.templateDigest ?? '');
export const selectTemplateChangedButHasNotSaved = createSelector(
  selectDigest,
  (slice) => !!slice.templateChangedButHasNotSaved,
);
